import { SIGNABLE_ACTS, SignableRecordType, type SignableAct } from '@qualicloud/contracts';

/**
 * QUAL REGISTRO ESTÁ SENDO ASSINADO.
 *
 * O `recordId` que o diálogo manda ao IDENTITY para EMITIR a prova precisa ser
 * byte a byte o mesmo que o CORE usa para CONSUMI-LA. `executeSignedAct`
 * compara os dois (`signatures.service.ts`) e, divergindo, lança
 * `SIGNATURE.PROOF_RECORD_MISMATCH` — só que a prova é de uso único e já foi
 * queimada nesse ponto. O usuário digita a senha, falha, digita de novo e falha
 * igual: era o estado de "Obsoletar" e de "Confirmar ciência".
 *
 * Esta função existe para que a correspondência seja UMA regra, num lugar só, e
 * testável sem montar a tela — e para que ela possa ser verificada contra o
 * catálogo `SIGNABLE_ACTS`, que é quem declara o `recordType` de cada ato.
 */
export interface SignedActRecordCandidates {
  /** `document_version.id` da versão vigente ou da versão em julgamento. */
  readonly documentVersionId: string | null;
  /** `document_acknowledgement.id` da MINHA pendência de ciência. */
  readonly acknowledgementId: string | null;
  /**
   * SGQ — Onda 2.
   *
   * Em todos os atos do SGQ o `recordId` é o AGREGADO, nunca a linha filha:
   * a avaliação, a verificação de eficácia e a ciência da circular só nascem
   * DENTRO da transação do ato, e a prova precisa ser pedida antes disso, com
   * um identificador que o cliente já conheça. É o que
   * `nonconformity.service.ts:548`, `capa.service.ts:804`,
   * `meetings.service.ts:553` e `circulars.service.ts:348` declaram, cada um com
   * o comentário correspondente. O `recordType` é que diz qual ato foi
   * praticado sobre o agregado.
   */
  readonly nonconformityId?: string | null;
  readonly capaActionId?: string | null;
  readonly meetingId?: string | null;
  readonly circularId?: string | null;
}

/**
 * Devolve o `recordId` do ato, ou `null` quando o registro correspondente não
 * está disponível — nesse caso o ato não pode ser oferecido ao usuário, porque
 * pedir a prova sem o registro certo é garantir a falha.
 */
export function signedActRecordId(
  act: SignableAct,
  candidates: SignedActRecordCandidates,
): string | null {
  const recordType = SIGNABLE_ACTS[act].recordType;

  switch (recordType) {
    case SignableRecordType.DOCUMENT_VERSION:
      return candidates.documentVersionId;
    case SignableRecordType.DOCUMENT_ACKNOWLEDGEMENT:
      // A ciência é individual: o registro assinado é a LINHA DE PENDÊNCIA do
      // colaborador, não o documento nem a versão.
      return candidates.acknowledgementId;

    // ── SGQ — Onda 2 ─────────────────────────────────────────────────────────
    case SignableRecordType.NONCONFORMITY:
    case SignableRecordType.NC_EVALUATION:
      return candidates.nonconformityId ?? null;
    /*
     * `EFFECTIVENESS_CHECK` devolve o id da AÇÃO, e a assimetria é deliberada:
     * a linha de `effectiveness_check` só nasce DENTRO da transação do ato
     * assinado, então o registro que o cliente consegue nomear ao pedir a prova
     * é a ação. O CORE consome exatamente assim
     * (`capa.service.ts` — `recordId: actionId`).
     *
     * Não existe `case` para `CAPA_ACTION`: nenhum ato do catálogo declara esse
     * `recordType`, e o `switch` é sobre `SIGNABLE_ACTS[act].recordType`. O ramo
     * era inalcançável e o compilador acusava.
     */
    case SignableRecordType.EFFECTIVENESS_CHECK:
      return candidates.capaActionId ?? null;
    case SignableRecordType.MEETING:
      return candidates.meetingId ?? null;
    case SignableRecordType.CIRCULAR_ACKNOWLEDGEMENT:
      // Diferente da ciência de DOCUMENTO: aqui o registro é a CIRCULAR, porque
      // a linha de `circular_acknowledgement` só nasce dentro da transação do
      // ato. O `UNIQUE` por destinatário é que garante uma assinatura por
      // pessoa (`circulars.service.ts:344-348`).
      return candidates.circularId ?? null;

    default:
      // Ato de módulo ainda não coberto (gestão de risco, reconciliação de
      // migração). Quem o emitir declara o próprio mapeamento aqui.
      return null;
  }
}
